import { UserType } from "encamp-prisma/generated/browser";

/** Stands in for an Encamp employee's name wherever a customer can see it. */
export const ENCAMP_STAFF_DISPLAY_NAME = "Encamp Staff";

/**
 * Mirrors `STAFF_USER_TYPES` from `encamp-shared/auth`, which cannot be
 * imported here because the auth module pulls in the server-only prisma
 * client and this module must stay browser-safe.
 */
const STAFF_USER_TYPES: ReadonlyArray<string> = [UserType.STAFF_ADMIN];

export type NamedUser = {
  email?: string | null;
  person?: { first?: string | null; last?: string | null } | null;
};

export type UserForStaffDisplay = NamedUser & { type: string };

export function isStaffUserType(type: string): boolean {
  return STAFF_USER_TYPES.includes(type);
}

export function getVisibleWaitingOnUsers<TUser extends UserForStaffDisplay>(
  users: TUser[],
  { viewerIsStaff }: { viewerIsStaff: boolean }
): TUser[] {
  if (viewerIsStaff) {
    return users;
  }
  return users.filter((user) => !isStaffUserType(user.type));
}

/** Full name, falling back to the email address, then to an empty string. */
export function getUserDisplayName(user: NamedUser | null | undefined): string {
  if (!user) {
    return "";
  }
  const name = `${user.person?.first ?? ""} ${user.person?.last ?? ""}`.trim();
  return name || user.email || "";
}

/** Identifies a user in activity records even when several people share a name. */
export function getUserDisplayNameWithEmail(user: NamedUser): string {
  const name = getUserDisplayName(user);
  return user.email && name !== user.email ? `${name} (${user.email})` : name;
}

/**
 * Display names for the users a submission is waiting on.
 *
 * Customers must not learn which employee holds their submission, but a blank
 * column while it sits in Encamp review reads as "waiting on nobody", so staff
 * collapse to a single `Encamp Staff` entry rather than being dropped.
 */
export function getWaitingOnDisplayNames(
  users: UserForStaffDisplay[],
  { viewerIsStaff }: { viewerIsStaff: boolean }
): string[] {
  return getWaitingOnDisplayUsers(users, { viewerIsStaff }).map((entry) => entry.displayName);
}

type WaitingOnDisplayUser<TUser> = { displayName: string; user: TUser | null };

/** {@link getWaitingOnDisplayNames}, keeping the user behind each name; null for the stand-in. */
export function getWaitingOnDisplayUsers<TUser extends UserForStaffDisplay>(
  users: TUser[],
  { viewerIsStaff }: { viewerIsStaff: boolean }
): WaitingOnDisplayUser<TUser>[] {
  let includesStaffPlaceholder = false;

  return users.flatMap((user): WaitingOnDisplayUser<TUser>[] => {
    if (!viewerIsStaff && isStaffUserType(user.type)) {
      if (includesStaffPlaceholder) {
        return [];
      }
      includesStaffPlaceholder = true;
      return [{ displayName: ENCAMP_STAFF_DISPLAY_NAME, user: null }];
    }

    const displayName = getUserDisplayName(user);
    return displayName ? [{ displayName, user }] : [];
  });
}
